import type { ReactNode } from "react";

export type Guide = {
  slug: string;
  title: string;
  description: string;
  readingTime: string;
  publishedAt: string;
  updatedAt: string;
  body: ReactNode;
};

export const guides: Guide[] = [
  {
    slug: "como-regular-toca-discos",
    title: "Como regular o toca-discos: peso do braço, antiskating e VTA",
    description:
      "Guia prático para colecionadores de vinil ajustarem peso de tracking, antiskating e VTA do toca-discos sem danificar agulha nem discos de vinil.",
    readingTime: "7 min",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-20",
    body: (
      <>
        <p>
          Regular o toca-discos é o que separa um som limpo de uma agulha que come os sulcos dos seus
          discos de vinil. Mesmo prensagens novas perdem definição se o braço estiver desalinhado.
          Este guia cobre os três ajustes que todo colecionador de vinil deveria saber fazer em casa:
          peso de tracking, antiskating e VTA.
        </p>

        <h2>1. Balanceie o braço antes de qualquer coisa</h2>
        <p>
          Antes de regular peso, o braço precisa flutuar paralelo ao prato. Remova o protetor da
          agulha, gire o contrapeso até que o braço fique em equilíbrio neutro — sem cair para frente
          nem subir. Esse é o "zero" do seu toca-discos.
        </p>

        <h2>2. Peso de tracking (Vertical Tracking Force)</h2>
        <p>
          Cada cápsula tem uma faixa recomendada pelo fabricante, geralmente entre 1,5 g e 2,2 g.
          Sem segurar o contrapeso, gire o anel numerado até o valor sugerido. Use uma balança
          digital de agulha (custa pouco e dura a vida toda) para confirmar — o anel é só uma
          referência.
        </p>
        <ul>
          <li>Peso baixo demais: agulha pula, distorce e arranha os sulcos.</li>
          <li>Peso alto demais: comprime o vinil e gasta a agulha mais rápido.</li>
        </ul>

        <h2>3. Antiskating</h2>
        <p>
          Quando o disco gira, a agulha tende a ser puxada para o centro. O antiskating compensa
          essa força. Comece igualando o valor de antiskating ao do peso de tracking (ex.: 2 g → 2)
          e ajuste pelo ouvido: se um canal soa mais sujo que o outro, o antiskating está fora.
        </p>

        <h2>4. VTA (Vertical Tracking Angle)</h2>
        <p>
          O braço deve ficar paralelo ao disco durante a reprodução. Coloque um disco de vinil de
          peso normal (140–180 g) e observe lateralmente. Se a base do cartucho estiver inclinada,
          ajuste a altura do pivô do braço até nivelar. Discos de 180 g pedem o braço um pouco mais
          alto que prensagens finas.
        </p>

        <h2>Quando refazer o ajuste</h2>
        <p>
          Sempre que trocar a agulha, mudar o mat do prato, ou tocar prensagens de gramaturas muito
          diferentes. Um toca-discos bem regulado preserva tanto o som quanto o valor da sua
          coleção de discos de vinil.
        </p>

        <p>
          Catalogou tudo numa planilha? Migre sua coleção para o{" "}
          <a href="/" className="underline">
            viny.club
          </a>{" "}
          — comunidade para colecionadores de vinil com catálogo visual, estatísticas e link na bio.
        </p>
      </>
    ),
  },
  {
    slug: "como-limpar-discos-de-vinil",
    title: "Como limpar discos de vinil antigos sem danificar",
    description:
      "Métodos seguros para limpar discos de vinil de segunda mão: escovas de carbono, solução com álcool isopropílico, máquinas de limpeza e o que nunca usar.",
    readingTime: "8 min",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-20",
    body: (
      <>
        <p>
          Comprou um lote de discos de vinil em feira ou herdou a coleção do tio? Antes de tocar,
          limpe. Poeira e gordura misturadas viram pasta abrasiva que arranha o sulco em poucas
          rotações. Este guia mostra três níveis de limpeza, do mais rápido ao mais profundo.
        </p>

        <h2>Limpeza rápida: escova de fibra de carbono</h2>
        <p>
          Para discos que parecem limpos mas têm pó superficial. Com o prato girando, encoste a
          escova de carbono levemente, deixe duas voltas, depois deslize para fora. Faça antes de
          cada audição — preserva a agulha e o sulco.
        </p>

        <h2>Limpeza média: solução com álcool isopropílico</h2>
        <p>Misture, em um borrifador pequeno:</p>
        <ul>
          <li>75% água destilada</li>
          <li>25% álcool isopropílico 99%</li>
          <li>1 gota de detergente neutro sem perfume</li>
        </ul>
        <p>
          Borrife sobre o disco (evite o rótulo), espalhe com escova de microfibra em movimentos
          circulares acompanhando o sulco, e seque com um pano de microfibra limpo. Deixe secar ao
          ar por 5 minutos antes de tocar.
        </p>

        <h2>Limpeza profunda: máquina de limpeza a vácuo</h2>
        <p>
          Para discos de vinil muito sujos, mofados ou de garimpo. Máquinas como a Pro-Ject VC-S ou
          a Knosti Disco-Antistat (manual e barata) removem partículas que escova nenhuma alcança.
          Vale o investimento se você compra usado com frequência.
        </p>

        <h2>O que nunca fazer</h2>
        <ul>
          <li>Lavar na pia com água da torneira — minerais deixam resíduo no sulco.</li>
          <li>Usar álcool puro — resseca o vinil ao longo do tempo.</li>
          <li>Esfregar com pano seco — o atrito arranha.</li>
          <li>Limpar o rótulo — molhar o papel descola e mancha.</li>
        </ul>

        <h2>Depois da limpeza, troque a capa interna</h2>
        <p>
          Capas internas de papel velhas riscam o disco. Troque por capas antiestáticas de papel +
          polietileno. Custam pouco e fazem diferença a longo prazo na coleção de discos de vinil.
        </p>

        <p>
          Quer registrar o estado de cada disco da sua coleção? O{" "}
          <a href="/" className="underline">
            viny.club
          </a>{" "}
          permite catalogar com notas de condição e fotos — comunidade para colecionadores de vinil.
        </p>
      </>
    ),
  },
  {
    slug: "como-armazenar-discos-de-vinil",
    title: "Como armazenar discos de vinil: temperatura, posição e capas",
    description:
      "Aprenda a armazenar discos de vinil corretamente: posição vertical, temperatura, umidade, capas internas e externas para preservar a coleção por décadas.",
    readingTime: "6 min",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-20",
    body: (
      <>
        <p>
          Discos de vinil são mais resistentes do que parecem, mas calor, umidade e armazenamento
          errado deformam o PVC e degradam as capas. Algumas regras simples mantêm a coleção
          impecável por décadas.
        </p>

        <h2>Sempre na vertical, nunca empilhados</h2>
        <p>
          Discos empilhados se entortam com o próprio peso. Guarde sempre em pé, em estantes que
          dêem suporte lateral. Evite inclinação: a fileira deve ficar reta como livros bem
          arrumados.
        </p>

        <h2>Temperatura e umidade</h2>
        <ul>
          <li>Temperatura ideal: entre 18 °C e 24 °C.</li>
          <li>Umidade ideal: 35% a 50%.</li>
          <li>Nunca: perto de janelas com sol direto, radiadores, ou cozinha.</li>
        </ul>
        <p>
          Calor empena o disco; umidade alta cria mofo nas capas. Em apartamentos brasileiros,
          ventile o cômodo e evite a parede mais quente.
        </p>

        <h2>Capa interna antiestática</h2>
        <p>
          Substitua capas internas originais de papel puro por antiestáticas (papel + polietileno
          MoFi, Nagaoka ou similares). Reduzem estática, retêm menos poeira e não arranham.
        </p>

        <h2>Capa externa de proteção</h2>
        <p>
          Uma capa plástica externa (PP de 3-mil ou PVC) protege a arte original de poeira e
          fricção. Importante para prensagens raras, primeiras edições e álbuns autografados.
        </p>

        <h2>Organização que preserva</h2>
        <p>
          Mantenha o disco fora da capa externa ao guardar (com a abertura da capa interna virada
          para cima). Isso evita que o disco saia do conjunto e caia. Catalogue tudo num sistema
          que aceite notas de condição — facilita seguro, venda e trocas.
        </p>

        <p>
          Procurando uma{" "}
          <a href="/" className="underline">
            comunidade para colecionadores de vinil
          </a>
          ? O viny.club tem catálogo, link na bio e leaderboard — feito por quem coleciona.
        </p>
      </>
    ),
  },
];

export function getGuide(slug: string): Guide | undefined {
  return guides.find((g) => g.slug === slug);
}
